import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useShippingPolicy } from '@/lib/hooks/useShippingPolicy';
import { formatCurrency } from '@/lib/utils';
import { storeAtom } from '@/states/store.state';
import { useAtomValue } from 'jotai';
import { InfoIcon, Truck } from 'lucide-react';

interface ShippingHeaderInfosProps {
  dealId: string;
}

export function ShippingHeaderInfos({ dealId }: ShippingHeaderInfosProps) {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const { shippingPolicy } = useShippingPolicy(dealId);

  const {
    minimumOrderEnabled,
    timeLimitedEnabled,
    minimumOrderAmount,
    minimumOrderSplitByDeliveryMode,
    pickupPointEnabled,
    pickupPointMinimumOrderAmount,
    pickupPointDeliveryPrice,
    offerDuration,
    allShippingFeesZero,
  } = shippingPolicy;
  const bothOptionsEnabled = minimumOrderEnabled && timeLimitedEnabled;
  const isPickupPointDeliveryFree =
    pickupPointEnabled && pickupPointDeliveryPrice === 0;
  const isPolicyActive =
    minimumOrderEnabled ||
    timeLimitedEnabled ||
    allShippingFeesZero ||
    isPickupPointDeliveryFree;

  const noShippingText = t('ui-v2.settings.blocks-shipping-policy.no-shipping');
  const hasHomeMinimum = minimumOrderAmount !== null;
  const hasPickupPointMinimum =
    pickupPointEnabled &&
    pickupPointDeliveryPrice !== 0 &&
    pickupPointMinimumOrderAmount !== null;
  const hasSplitMinimum =
    pickupPointEnabled &&
    minimumOrderSplitByDeliveryMode &&
    (hasHomeMinimum || hasPickupPointMinimum || isPickupPointDeliveryFree);
  const getSplitMinimumText = (compact = false) =>
    [
      hasHomeMinimum
        ? t(
            `ui-v2.settings.blocks-shipping-policy.free-shipping.home-minimum${compact ? '-short' : ''}`,
            { amount: formatCurrency(minimumOrderAmount, store) }
          )
        : t(
            `ui-v2.settings.blocks-shipping-policy.free-shipping.home-paid${compact ? '-short' : ''}`
          ),
      hasPickupPointMinimum
        ? t(
            `ui-v2.settings.blocks-shipping-policy.free-shipping.pickup-point-minimum${compact ? '-short' : ''}`,
            { amount: formatCurrency(pickupPointMinimumOrderAmount, store) }
          )
        : t(
            `ui-v2.settings.blocks-shipping-policy.free-shipping.${isPickupPointDeliveryFree ? 'pickup-point-offered' : 'pickup-point-paid'}${compact ? '-short' : ''}`
          ),
    ].join(' · ');
  const minimumText = hasSplitMinimum
    ? getSplitMinimumText()
    : formatCurrency(minimumOrderAmount, store);
  const minimumSummary = hasSplitMinimum
    ? `${t(
        'ui-v2.settings.blocks-shipping-policy.free-shipping.minimum-prefix'
      )} · ${getSplitMinimumText(true)}`
    : t(
        'ui-v2.settings.blocks-shipping-policy.free-shipping.minimum-only-trigger',
        { minimum: minimumText }
      );
  const durationSummary = t(
    'ui-v2.settings.blocks-shipping-policy.free-shipping.duration-only-trigger',
    { duration: offerDuration }
  );

  const triggerText = allShippingFeesZero
    ? t(
        'ui-v2.settings.blocks-shipping-policy.free-shipping.no-conditions.trigger'
      )
    : bothOptionsEnabled
      ? `${durationSummary} - ${minimumSummary}`
      : minimumOrderEnabled
        ? minimumSummary
        : timeLimitedEnabled
          ? durationSummary
          : isPickupPointDeliveryFree
            ? t(
                'ui-v2.settings.blocks-shipping-policy.free-shipping.pickup-point-offered-short'
              )
            : noShippingText;

  const tooltipText = allShippingFeesZero
    ? t(
        'ui-v2.settings.blocks-shipping-policy.free-shipping.no-conditions.description',
        {
          currency: formatCurrency(0, store),
        }
      )
    : bothOptionsEnabled
      ? t(
          'ui-v2.settings.blocks-shipping-policy.free-shipping.both-options-enabled-tooltip',
          {
            minimum: minimumText,
            duration: offerDuration,
          }
        )
      : minimumOrderEnabled
        ? t(
            'ui-v2.settings.blocks-shipping-policy.free-shipping.minimum-only',
            {
              minimum: minimumText,
            }
          )
        : timeLimitedEnabled
          ? t(
              'ui-v2.settings.blocks-shipping-policy.free-shipping.duration-only',
              {
                duration: offerDuration,
              }
            )
          : isPickupPointDeliveryFree
            ? t(
                'ui-v2.settings.blocks-shipping-policy.free-shipping.pickup-point-offered'
              )
            : noShippingText;

  return (
    <div data-user-guide="Shipping Policy">
      {isPolicyActive ? (
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="hover:text-bold flex cursor-pointer items-center gap-1 rounded-sm hover:bg-gray-50">
                  <Truck className="h-4 w-4" />

                  <Link to={`/deal/${dealId}/settings`}>
                    <p>{triggerText}</p>
                  </Link>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={10}>
                <div className="text-center">{tooltipText}</div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ) : (
        <div className="flex items-center gap-1 rounded-sm text-red-400 hover:cursor-pointer hover:bg-gray-50 hover:text-red-700">
          <InfoIcon className="h-4 w-4 text-red-500" />

          <Link to={`/deal/${dealId}/settings`}>
            <p>{tooltipText}</p>
          </Link>
        </div>
      )}
    </div>
  );
}
